import { RecaptchaProvider } from '@calendly/recaptcha'
import { SafeSuspense } from '@calendly/safe-suspense'
import { Modal } from '@calendly/ui/components/modal'
import PropTypes from 'prop-types'
import { lazy, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Settings from 'core/settings'

import {
  eventTypeIdSelector,
  eventTypeOwnerIdSelector,
  eventTypeOwnerTypeSelector,
  eventTypeTypeSelector,
  modalVisibleSelector,
} from '../selectors'
import { closeModal, fetchBookEventType } from '../slice'
import { CloseButton, ModalContent, StyledLoader } from './styles'

const loadChunk = async () => {
  const loaded = await import(
    /* webpackChunkName: 'book_modal' */ './modal_body'
  )
  return { default: loaded.ModalBody }
}

const ModalBody = lazy(loadChunk)

export const BookModal = ({
  onClose: childClose = () => { },
  withClose = false
}) => {
  const ref = useRef()
  const dispatch = useDispatch()
  const modalVisible = useSelector(modalVisibleSelector)
  const eventTypeId = useSelector(eventTypeIdSelector)
  const eventTypeType = useSelector(eventTypeTypeSelector)
  const eventTypeOwnerType = useSelector(eventTypeOwnerTypeSelector)
  const eventTypeOwnerId = useSelector(eventTypeOwnerIdSelector)

  useEffect(() => {
    if (modalVisible && eventTypeId) {
      dispatch(
        fetchBookEventType({
          eventTypeId,
          eventTypeType,
          eventTypeOwnerType,
          eventTypeOwnerId,
        })
      )
      document.activeElement?.blur()
    }
  }, [
    modalVisible,
    eventTypeId,
    eventTypeType,
    eventTypeOwnerType,
    eventTypeOwnerId,
    dispatch,
  ])

  const onClose = useCallback(() => {
    dispatch(closeModal())
    childClose()
    return true
  }, [dispatch, childClose])

  const handlePopState = useCallback((event) => {
    event.preventDefault()
    window.removeEventListener('popstate', handlePopState)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (modalVisible) {
      window.addEventListener('popstate', handlePopState)
    }
  }, [modalVisible, onClose, handlePopState])

  return (
    <RecaptchaProvider
      siteKey={Settings.recaptcha.site_key}
      isActive={!!Settings.recaptcha.activated}
      siteKeyV2={Settings.recaptcha.site_key_v2}
      isActiveV2={!!Settings.recaptcha.activated_v2}
    >
      <Modal ref={ref} isOpen={modalVisible} onClose={onClose}>
        <ModalContent>
          {withClose && <CloseButton onClick={onClose} />}
          <SafeSuspense fallback={<StyledLoader />}>
            <ModalBody modalRef={ref} onClose={onClose} />
          </SafeSuspense>
        </ModalContent>
      </Modal>
    </RecaptchaProvider>
  )
}

BookModal.propTypes = {
  onClose: PropTypes.func,
  withClose: PropTypes.bool,
}
